import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah } from "@/lib/format";
import FilterBar from "@/components/FilterBar";
import DailySummaryCard from "@/components/DailySummaryCard";
import FollowUpTodayWidget from "@/components/FollowUpTodayWidget";
import ScheduledActivitiesWidget from "@/components/ScheduledActivitiesWidget";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, AreaChart, Area, CartesianGrid } from "recharts";
import ConversionFunnel from "@/components/ConversionFunnel";
import { Users2, TrendingUp, Wallet, Trophy, TriangleAlert, Send, Kanban } from "lucide-react";
import { getGreeting } from "@/lib/greeting";

const COLORS = ["#3B82F6", "#22D3EE", "#F59E0B", "#10B981", "#EF4444", "#6366F1", "#EC4899"];
const ts = { background: "#18181B", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 };
// Recharts does not read contentStyle for the label/item text color — without these,
// tooltip text silently inherits the page's default (near-black in light mode),
// landing dark-on-dark against the tooltip's own dark background.
const tsItem = { color: "#e5e7eb" };
const tsLabel = { color: "#93c5fd" };

// Always the same dark-navy card as the Total Omset hero, regardless of light/dark theme
// — this KPI grid shares the hero's color identity instead of washing out to a pale
// per-category tint in light mode. See the .dash-dark exemption block in index.css.
const DASH_CARD_STYLE = { border: "1px solid rgba(96,165,250,0.16)", backgroundImage: "linear-gradient(155deg, #111a2e 0%, #0c1120 60%, #070a12 100%)" };

function KpiCard({ icon: Icon, label, value, accent, sub, testid }) {
  return (
    <div className="dash-dark rounded-2xl p-5" style={DASH_CARD_STYLE} data-testid={testid}>
      <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ background: `${accent}22`, color: accent }}><Icon size={20} /></div>
      {/* clamp() shrinks large figures (e.g. Rp 1.000.000.000.000) to fit instead of
          wrapping mid-number — nowrap+ellipsis is the last-resort safety net if a value
          is still too wide even at the smallest clamped size. */}
      <div className="font-bold font-heading tracking-tight whitespace-nowrap overflow-hidden text-ellipsis"
        style={{ fontSize: "clamp(0.7rem, 2.5vw, 0.88rem)", fontVariantNumeric: "tabular-nums" }} title={typeof value === "string" ? value : undefined}>
        {value}
      </div>
      <div className="text-xs text-zinc-500 mt-0.5">{label}</div>
      {sub && <div className="text-[11px] text-zinc-600 mt-1">{sub}</div>}
    </div>
  );
}

// Two stacked numbers sharing one card slot instead of two separate boards — overall
// lead count (every stage, all-time within the selected period) on top, and how many of
// those are still actively being worked (Konsultasi/Survei/SPH — not yet Closing/Lost)
// underneath.
function TotalLeadCard({ total, active, accent, testid }) {
  const Icon = Kanban;
  return (
    <div className="dash-dark rounded-2xl p-5" style={DASH_CARD_STYLE} data-testid={testid}>
      <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ background: `${accent}22`, color: accent }}><Icon size={20} /></div>
      <div className="font-bold font-heading tracking-tight whitespace-nowrap overflow-hidden text-ellipsis"
        style={{ fontSize: "clamp(0.65rem, 2.3vw, 0.8rem)", fontVariantNumeric: "tabular-nums" }} title={String(total)}>
        {total}
      </div>
      <div className="text-[11px] text-zinc-500 mt-0.5">Total Lead Keseluruhan</div>
      <div className="h-px bg-white/10 my-2" />
      <div className="font-bold font-heading tracking-tight whitespace-nowrap overflow-hidden text-ellipsis"
        style={{ fontSize: "clamp(0.65rem, 2.3vw, 0.8rem)", fontVariantNumeric: "tabular-nums" }} title={String(active)}>
        {active}
      </div>
      <div className="text-[11px] text-zinc-500 mt-0.5">Lead Aktif <span className="text-zinc-600">(Konsultasi/Survei/SPH)</span></div>
    </div>
  );
}

// Small stat pill for inside the mobile hero — same visual language regardless of theme
// (the hero itself is a fixed dark navy, never white/pale, so its children stay legible
// in both light and dark mode without any theme-conditional styling here). Muted surface
// (not a bright white overlay) to match the hero's restrained, non-"norak" tone.
function StatPill({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-center gap-2 bg-white/[0.06] border border-white/10 rounded-xl px-2.5 py-2 min-w-0" data-testid={testid}>
      <Icon size={14} className="text-blue-300/80 shrink-0" />
      <div className="min-w-0">
        <div className="text-[13px] font-bold font-heading text-white truncate leading-tight" style={{ fontVariantNumeric: "tabular-nums" }} title={typeof value === "string" ? value : undefined}>{value}</div>
        <div className="text-[10px] text-blue-300/50 truncate leading-tight">{label}</div>
      </div>
    </div>
  );
}

// A row inside the desktop stat list — icon badge + label on the left, value on the
// right, thin divider between rows. Same "small colored icon on a neutral surface"
// language as the reference dashboards (Aura Bank's transaction list, Findexa's cards),
// not a colored background wash.
function StatRow({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="flex items-center justify-between gap-2 py-2.5 border-b border-white/5 last:border-0" data-testid={testid}>
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}1f`, color: accent }}><Icon size={15} /></div>
        <span className="text-sm text-zinc-400 truncate">{label}</span>
      </div>
      <span className="text-sm font-bold font-heading whitespace-nowrap shrink-0" style={{ fontVariantNumeric: "tabular-nums" }} title={typeof value === "string" ? value : undefined}>{value}</span>
    </div>
  );
}

function Panel({ title, children, className = "", compact = false }) {
  return (
    <div className={`card-surface rounded-2xl ${compact ? "p-3" : "p-5"} ${className}`}>
      <h3 className={`font-heading font-semibold text-zinc-400 uppercase tracking-wide ${compact ? "text-[10px] mb-2" : "text-sm mb-4"}`}>{title}</h3>
      {children}
    </div>
  );
}

// The 8-month omset trend curve — shared between the mobile hero (shorter) and the
// desktop bento hero (taller), so both get the same "real shape, not a flat line" chart
// instead of duplicating the gradient/glow setup twice.
function OmsetTrendChart({ trend, height = 100, idPrefix = "a" }) {
  if (!trend?.some((m) => m.value > 0)) return null;
  const curveId = `omsetHeroCurve-${idPrefix}`;
  const strokeId = `omsetHeroStroke-${idPrefix}`;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={trend} margin={{ top: 6, right: 8, left: 8, bottom: 0 }}>
        <defs>
          <linearGradient id={curveId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.65} />
            <stop offset="60%" stopColor="#38bdf8" stopOpacity={0.12} />
            <stop offset="100%" stopColor="#38bdf8" stopOpacity={0} />
          </linearGradient>
          <linearGradient id={strokeId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#0ea5e9" />
            <stop offset="55%" stopColor="#38bdf8" />
            <stop offset="100%" stopColor="#7dd3fc" />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke="rgba(148,163,184,0.09)" />
        <XAxis dataKey="name" tick={{ fill: "#475569", fontSize: 9.5 }} axisLine={false} tickLine={false} interval={0} />
        <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(56,189,248,0.25)", borderRadius: 10, fontSize: 11 }} itemStyle={tsItem} labelStyle={tsLabel} formatter={(v) => rupiah(v)} cursor={{ stroke: "rgba(56,189,248,0.35)", strokeWidth: 1 }} />
        <Area type="monotone" dataKey="value" stroke={`url(#${strokeId})`} strokeWidth={2.5} fill={`url(#${curveId})`}
          dot={{ r: 2.5, fill: "#38bdf8", strokeWidth: 0 }} activeDot={{ r: 5, fill: "#7dd3fc", stroke: "#0f172a", strokeWidth: 2 }}
          isAnimationActive={false}
          style={{ filter: "drop-shadow(0 0 7px rgba(56,189,248,0.55))" }} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [qs, setQs] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["dashboard", qs], queryFn: async () => (await api.get(`/dashboard?${qs}`)).data });

  const agingColors = { new: "#10B981", normal: "#3B82F6", attention: "#F59E0B", critical: "#EF4444" };
  // Sales already only ever receives their own scoped numbers from the backend
  // (scope_query() filters /dashboard to their own leads) — this just makes the label
  // honest about whose total it is, instead of implying company-wide when it isn't.
  const omsetHeroLabel = user?.role === "sales" ? "Total Omset Kamu" : "Total Omset Keseluruhan";
  const greeting = getGreeting(user?.name?.split(" ")[0]);

  return (
    <div className="px-4 py-5 max-w-6xl mx-auto print-area md:max-w-none md:px-8">
      <div className="mb-4">
        <h1 className="text-lg lg:text-2xl font-bold font-heading tracking-tight">{greeting.label}, {user?.name?.split(" ")[0]} {greeting.emoji}</h1>
        <p className="text-xs lg:text-sm text-zinc-500">{greeting.note}</p>
      </div>
      <div className="no-print"><FilterBar onChange={setQs} /></div>

      {isLoading || !data ? null : (() => {
        const k = data.kpis;
        const agingData = Object.entries(data.aging).map(([key, v]) => ({ name: { new: "Baru", normal: "Normal", attention: "Perhatian", critical: "Kritis" }[key], value: v, key }));
        return (
          <>
            {/* Mobile hero — a compact headline balance, single column, so a dominant card
                makes sense the way it does in a phone finance app. Muted near-black navy
                (not a loud saturated blue), a fine hairline grid for a bit of technical
                texture, and a restrained glow instead of a big flat color block. Hidden
                on desktop entirely — see the compact 5-card row below for that layout,
                which is deliberately a different composition, not this same block stretched. */}
            <div className="lg:hidden rounded-2xl p-4 mb-4 relative overflow-hidden" data-testid="kpi-hero"
              style={{
                border: "1px solid rgba(96,165,250,0.18)",
                backgroundImage: "linear-gradient(155deg, #111a2e 0%, #0c1120 60%, #070a12 100%), repeating-linear-gradient(115deg, rgba(255,255,255,0.035) 0px, rgba(255,255,255,0.035) 1px, transparent 1px, transparent 14px)",
              }}>
              <div className="absolute -right-8 -top-12 w-40 h-40 rounded-full blur-3xl opacity-[0.15] pointer-events-none" style={{ background: "#3b82f6" }} />
              <div className="relative">
                <p className="text-blue-300/60 text-[10px] uppercase tracking-wider">{omsetHeroLabel}</p>
                <p className="font-bold font-heading text-white mt-1 tracking-tight whitespace-nowrap overflow-hidden text-ellipsis"
                  style={{ fontSize: "clamp(1.15rem, 6vw, 1.75rem)", fontVariantNumeric: "tabular-nums" }}>
                  {rupiah(k.omset_gabungan ?? k.total_omset ?? 0)}
                </p>
                <div className="-mx-1 mt-1"><OmsetTrendChart trend={data.omset_trend} height={90} idPrefix="mobile" /></div>
                <div className="grid grid-cols-2 gap-1.5 mt-3">
                  <StatPill icon={Trophy} label="Lead Closing" value={k.total_closing} testid="kpi-leads-aktif-hero" />
                  <StatPill icon={Send} label="SPH Diajukan" value={k.total_sph} testid="kpi-total-sph" />
                  <StatPill icon={Wallet} label="Total Piutang" value={rupiah(k.total_piutang_gabungan ?? k.total_piutang)} testid="kpi-total-piutang" />
                  <StatPill icon={TrendingUp} label="Closing Rate" value={`${k.closing_rate}%`} testid="kpi-closing-rate-mobile" />
                </div>
              </div>
            </div>

            {/* Desktop — an asymmetric bento layout instead of the mobile hero stretched
                wide: a large card carrying the headline number plus a compact inline chart
                (per-sales split), and a neutral stat list beside it — mixed proportions
                and a real chart, the way the reference dashboards actually compose a
                desktop layout rather than just scaling up the phone one. */}
            <div className="hidden lg:grid lg:grid-cols-3 gap-4 mb-4">
              <div className="lg:col-span-2 rounded-2xl p-6 relative overflow-hidden" data-testid="kpi-omset-desktop"
                style={{
                  border: "1px solid rgba(96,165,250,0.18)",
                  backgroundImage: "linear-gradient(155deg, #111a2e 0%, #0c1120 60%, #070a12 100%), repeating-linear-gradient(115deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 16px)",
                }}>
                <div className="absolute -right-10 -top-14 w-52 h-52 rounded-full blur-3xl opacity-[0.12] pointer-events-none" style={{ background: "#3b82f6" }} />
                <div className="relative flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-blue-300/60 text-xs uppercase tracking-wider">{omsetHeroLabel}</p>
                    <p className="font-bold font-heading text-white mt-1.5 tracking-tight whitespace-nowrap overflow-hidden text-ellipsis"
                      style={{ fontSize: "clamp(1.5rem, 3vw, 2.25rem)", fontVariantNumeric: "tabular-nums" }}>
                      {rupiah(k.omset_gabungan ?? k.total_omset ?? 0)}
                    </p>
                  </div>
                  <div className="flex gap-4 shrink-0">
                    <div className="text-right">
                      <div className="text-sm font-bold font-heading text-emerald-300">{k.total_closing}</div>
                      <div className="text-[10px] text-blue-300/50">Closing</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold font-heading text-blue-200">{k.closing_rate}%</div>
                      <div className="text-[10px] text-blue-300/50">Rate</div>
                    </div>
                  </div>
                </div>
                <div className="relative mt-5 -mx-2"><OmsetTrendChart trend={data.omset_trend} height={140} idPrefix="desktop" /></div>
              </div>

              <div className="card-surface rounded-2xl p-4">
                <StatRow icon={Trophy} label="Lead Closing" value={k.total_closing} accent="#22D3EE" testid="kpi-lead-desktop" />
                <StatRow icon={Send} label="SPH Diajukan" value={k.total_sph} accent="#F59E0B" testid="kpi-sph-desktop" />
                <StatRow icon={Wallet} label="Total Piutang" value={rupiah(k.total_piutang_gabungan ?? k.total_piutang)} accent="#EF4444" testid="kpi-piutang-desktop" />
                <StatRow icon={TrendingUp} label="Closing Rate" value={`${k.closing_rate}%`} accent="#10B981" testid="kpi-closing-desktop" />
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
              <TotalLeadCard total={k.total_leads || 0} active={k.total_active || 0} accent="#3B82F6" testid="kpi-total-lead" />
              <KpiCard icon={Trophy} label="Omset Perencanaan/Design" value={rupiah(k.omset_perencanaan ?? k.total_omset ?? k.total_sales_value)} accent="#10B981" sub={`${k.total_closing} closing`} />
              <KpiCard icon={Trophy} label="Omset Produksi" testid="kpi-omset-produksi" value={rupiah(k.omset_produksi || 0)} accent="#8B5CF6" />
              <KpiCard icon={Kanban} label="Lead Aktif (Konsultasi/SPH/Survei)" testid="kpi-pipeline-value" value={rupiah(k.pipeline_value || 0)} accent="#3B82F6" sub="nilai pipeline, belum jadi omset" />
              <KpiCard icon={Wallet} label="Sisa Piutang Produksi" testid="kpi-piutang-produksi" value={rupiah(k.total_piutang_produksi || 0)} accent="#EF4444" />
              <KpiCard icon={TriangleAlert} label="Lost Order" testid="kpi-lost-order" value={k.total_lost || 0} accent="#EF4444" sub={rupiah(k.total_lost_value || 0)} />
            </div>

            {user?.role !== "sales" && <DailySummaryCard />}
            <FollowUpTodayWidget />
            <ScheduledActivitiesWidget />

            <Panel title="Funnel Konversi (Konsultasi → SPH → Closing)" className="mb-3">
              <ConversionFunnel data={data.funnel} />
            </Panel>

            {/* Aging + Performa Sales side-by-side even on mobile — two compact columns
                instead of stacking full-width, so a phone screen doesn't need three
                consecutive scrolls to get past them. */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <Panel compact title="Leads Aging">
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={agingData} margin={{ left: -20 }}>
                    <XAxis dataKey="name" tick={{ fill: "#71717a", fontSize: 9 }} axisLine={false} tickLine={false} interval={0} />
                    <YAxis tick={{ fill: "#71717a", fontSize: 9 }} axisLine={false} tickLine={false} allowDecimals={false} width={22} />
                    <Tooltip contentStyle={ts} itemStyle={tsItem} labelStyle={tsLabel} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]} isAnimationActive={false}>{agingData.map((e) => <Cell key={e.key} fill={agingColors[e.key]} />)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Panel>

              <Panel compact title="Performa Sales">
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={data.per_sales.slice(0, 5)} layout="vertical" margin={{ left: -10 }}>
                    <XAxis type="number" tick={{ fill: "#71717a", fontSize: 8 }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="name" tick={{ fill: "#a1a1aa", fontSize: 8 }} axisLine={false} tickLine={false} width={50} tickFormatter={(v) => (v || "-").split(" ")[0]} />
                    <Tooltip contentStyle={ts} itemStyle={tsItem} labelStyle={tsLabel} formatter={(v) => rupiah(v)} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                    <Bar dataKey="value" fill="#3B82F6" radius={[0, 4, 4, 0]} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
            </div>

            {/* Insight per Channel + Analisis Lost Order — same paired treatment. */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <Panel compact title="Insight Channel">
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={data.per_channel.slice(0, 6)} dataKey="total" nameKey="name" cx="50%" cy="50%" outerRadius={52} innerRadius={30} isAnimationActive={false}>
                      {data.per_channel.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={ts} itemStyle={tsItem} labelStyle={tsLabel} />
                  </PieChart>
                </ResponsiveContainer>
              </Panel>

              <Panel compact title="Lost Order">
                <div className="mb-2 text-[11px] text-zinc-400">Lost: <span className="text-red-400 font-semibold">{rupiah(data.lost_revenue)}</span></div>
                <div className="space-y-1.5 max-h-[124px] overflow-y-auto hide-scrollbar">
                  {data.lost_reasons.length === 0 && <p className="text-[11px] text-zinc-600">Belum ada data</p>}
                  {data.lost_reasons.map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-[11px] gap-1">
                      <span className="flex items-center gap-1 text-zinc-300 truncate min-w-0"><TriangleAlert size={11} className="text-amber-500 shrink-0" /><span className="truncate">{r.name}</span></span>
                      <span className="text-zinc-500 shrink-0">{r.count}x</span>
                    </div>
                  ))}
                </div>
              </Panel>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <Panel title="Performa per Cabang" className="lg:col-span-2">
                <div className="space-y-2">
                  {data.per_branch.map((b, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-[#18181B]">
                      <div><div className="font-medium text-sm">{b.name}</div><div className="text-xs text-zinc-500">{b.total} leads · closing rate {b.closing_rate}%</div></div>
                      <div className="text-right"><div className="font-semibold text-sm text-emerald-400">{rupiah(b.value)}</div><div className="text-xs text-zinc-500">{b.closing} closing</div></div>
                    </div>
                  ))}
                </div>
              </Panel>

              <Panel title="Target vs Actual" className="lg:col-span-2">
                <div className="flex items-end gap-4 sm:gap-6 mb-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-xs text-zinc-500">Actual</div>
                    <div className="font-bold font-heading text-cyan-400 whitespace-nowrap overflow-hidden text-ellipsis" style={{ fontSize: "clamp(0.85rem, 2.6vw, 1.125rem)", fontVariantNumeric: "tabular-nums" }} title={rupiah(data.target_vs_actual.actual)}>{rupiah(data.target_vs_actual.actual)}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs text-zinc-500">Target</div>
                    <div className="font-bold font-heading text-zinc-400 whitespace-nowrap overflow-hidden text-ellipsis" style={{ fontSize: "clamp(0.85rem, 2.6vw, 1.125rem)", fontVariantNumeric: "tabular-nums" }} title={rupiah(data.target_vs_actual.target)}>{rupiah(data.target_vs_actual.target)}</div>
                  </div>
                </div>
                {(() => {
                  const pct = data.target_vs_actual.target ? Math.min(100, Math.round(data.target_vs_actual.actual / data.target_vs_actual.target * 100)) : 0;
                  return (<div><div className="h-3 rounded-full bg-[#18181B] overflow-hidden"><div className="h-full bg-gradient-to-r from-blue-700 to-blue-900 rounded-full transition-all" style={{ width: `${pct}%` }} /></div><div className="text-xs text-zinc-500 mt-1.5">{pct}% dari target tercapai</div></div>);
                })()}
              </Panel>
            </div>
          </>
        );
      })()}
    </div>
  );
}
